import React from 'react'
import { Button, Input, InputCheck } from 'src/components'
import { formatDate, formatDateToInputValue } from 'src/utils'
import { useFormContext } from 'react-hook-form'
import { CreateAnimalForm } from 'src/@types/validations/Animal'
import { PregnancyDiagnosisDto } from 'src/dto/pregnancy-diagnosis.dto'
import { SavedAnimal } from 'src/pages/Protected/Reproduction/components/SavedAnimal'
import ErrorOutline from '@material-ui/icons/ErrorOutline'
import { formParams } from '../..'
import { useStyles } from '../../styles'

interface DiagnosisSectionProps {
  hasDiagnosis: boolean
  showDiagnosisForm: boolean
  setShowDiagnosisForm: (v: boolean) => void
  hasBirthAnimalsFromDiagnosis: boolean
  diagnosisStatus?: string | null
  diagnosisDate?: string | null
  savedBirthAnimals: CreateAnimalForm[]
  pregnancyDiagnosis?: PregnancyDiagnosisDto | null
  handleOpenDiagnosisModal: (diagDate?: string, reproDate?: string) => void
  handleRemoveBirth?: (index: number) => void
  hasJustSaved?: boolean
}

export const DiagnosisSection: React.FC<DiagnosisSectionProps> = ({
  hasDiagnosis,
  showDiagnosisForm,
  setShowDiagnosisForm,
  hasBirthAnimalsFromDiagnosis,
  diagnosisStatus,
  diagnosisDate,
  savedBirthAnimals,
  pregnancyDiagnosis,
  handleOpenDiagnosisModal,
  handleRemoveBirth,
  hasJustSaved = false
}) => {
  const style = useStyles()
  const {
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors }
  } = useFormContext<formParams>()
  const today = new Date().toISOString().split('T')[0]

  const validateDateNotFuture = (value: string, fieldName: string, fieldLabel: string) => {
    const selectedDate = new Date(value)
    const todayDate = new Date(today)

    if (selectedDate > todayDate) {
      setError(fieldName as any, { message: `${fieldLabel} não pode ser superior à data de hoje` })
      return false
    }
    return true
  }

  return (
    <section className={`${style.section} ${style.diagnosisSection}`}>
      <div className={style.sectionHeader}>
        <h2 className={style.sectionTitle}>Diagnósticos</h2>
        {(hasBirthAnimalsFromDiagnosis || diagnosisStatus === 'vazia') && hasDiagnosis && (
          <div className={`${style.buttonWrapper} ${style.secondaryOutlinedField} ${style.deliveryDateDesktop}`}>
            Data do parto:{' '}
            {diagnosisStatus === 'vazia'
              ? 'Não informada'
              : formatDate(pregnancyDiagnosis?.dataParto! || '', false) || '-'}
          </div>
        )}
        {diagnosisStatus === 'prenha' &&
          !hasBirthAnimalsFromDiagnosis &&
          !hasJustSaved &&
          (hasDiagnosis || showDiagnosisForm) && (
            <Button
              type='button'
              className={`${style.buttonWrapper} ${style.buttonOutlined}`}
              onClick={() =>
                handleOpenDiagnosisModal(
                  watch('pregnancyDiagnosis.dataDiagnostico') || undefined,
                  watch('pregnancyDiagnosis.dataInicial') || pregnancyDiagnosis?.dataCobertura || undefined
                )
              }
              outlined
            >
              Cadastrar nascimento
            </Button>
          )}
      </div>

      {!hasDiagnosis && !showDiagnosisForm ? (
        <div className={style.pendingDiagnosisContainer}>
          <div className={style.pendingDiagnosisContent}>
            <ErrorOutline className={style.pendingDiagnosisIcon} />
            <span className={style.pendingDiagnosisText}>Diagnóstico pendente</span>
          </div>
          <Button
            type='button'
            className={style.buttonWrapper}
            onClick={() => {
              setValue('pregnancyDiagnosis.diagnostico', 'prenha', { shouldDirty: true })
              setShowDiagnosisForm(true)
            }}
          >
            Adicionar diagnóstico
          </Button>
        </div>
      ) : (
        <div className={style.diagnosisInfoContainer}>
          <div className={style.radioGroupInline}>
            <InputCheck
              id='diagnostico-prenha'
              type='radio'
              name='diagnosis_status_inline'
              value='prenha'
              label='Prenha'
              checked={diagnosisStatus === 'prenha'}
              onChange={() => setValue('pregnancyDiagnosis.diagnostico', 'prenha', { shouldDirty: true })}
            />
            <InputCheck
              id='diagnostico-vazia'
              type='radio'
              name='diagnosis_status_inline'
              value='vazia'
              label='Vazia'
              checked={diagnosisStatus === 'vazia'}
              onChange={() => setValue('pregnancyDiagnosis.diagnostico', 'vazia', { shouldDirty: true })}
            />
          </div>

          <div className={style.formControl}>
            <Input
              type='date'
              max={today}
              label='Data do diagnóstico'
              id='data_diagnostico'
              value={formatDateToInputValue(diagnosisDate)}
              error={errors?.pregnancyDiagnosis?.dataDiagnostico?.message}
              onChange={(value: string) => {
                if (validateDateNotFuture(value, 'pregnancyDiagnosis.dataDiagnostico', 'Data do diagnóstico')) {
                  clearErrors('pregnancyDiagnosis.dataDiagnostico')
                }
                setValue('pregnancyDiagnosis.dataDiagnostico', value, { shouldDirty: true })
              }}
            />
          </div>

          {(hasBirthAnimalsFromDiagnosis || diagnosisStatus === 'vazia') && hasDiagnosis && (
            <div className={`${style.buttonWrapper} ${style.secondaryOutlinedField} ${style.deliveryDateMobile}`}>
              Data do parto:{' '}
              {diagnosisStatus === 'vazia'
                ? 'Não informada'
                : formatDate(pregnancyDiagnosis?.dataParto! || '', false) || '-'}
            </div>
          )}

          {savedBirthAnimals.length > 0 &&
            savedBirthAnimals.map((savedBirthAnimal, index) => {
              const isSavedAnimal =
                !!(savedBirthAnimal.id || savedBirthAnimal.idAnimal) || hasJustSaved || hasBirthAnimalsFromDiagnosis
              return (
                <SavedAnimal
                  key={savedBirthAnimal.cdn || savedBirthAnimal.id || savedBirthAnimal.idAnimal || `new-${index}`}
                  savedBirthAnimal={savedBirthAnimal}
                  index={index}
                  onRemove={!isSavedAnimal && handleRemoveBirth ? () => handleRemoveBirth(index) : undefined}
                />
              )
            })}
        </div>
      )}
    </section>
  )
}
